import { getApiClient } from '@src/services/api-client'
import { MutationFunction, useMutation, useQueryClient } from '@tanstack/react-query'
import { CreditApplication, FullCreditApplication } from '../../types'
import { SetCustomerReferenceDto } from '../../types/CustomerReferenceSchema'
import { availableCreditKeys } from '../available-credit-api'
import { transformCreditApplicationFromApi } from '../credit-application/credit-transform'
import { fullCreditApplicationKeys } from '../full-credit-application/full-credit-application-api'

const applicantUrl = '/Applications/{financingProgramId}/{creditApplicationId}/Applicant/ExistingCustomer'
const coapplicantUrl = '/Applications/{financingProgramId}/{creditApplicationId}/Coapplicant/ExistingCustomer'

// API Functions
export async function setApplicantCustomerReference(data: SetCustomerReferenceDto): Promise<CreditApplication> {
  const apiClient = getApiClient()
  const url = applicantUrl
    .replace('{financingProgramId}', data.financingProgramId)
    .replace('{creditApplicationId}', data.creditApplicationId)
  const response = await apiClient.put<CreditApplication>(url, data)
  return response.data
}

export async function setCoapplicantCustomerReference(data: SetCustomerReferenceDto): Promise<CreditApplication> {
  const apiClient = getApiClient()
  const url = coapplicantUrl
    .replace('{financingProgramId}', data.financingProgramId)
    .replace('{creditApplicationId}', data.creditApplicationId)
  const response = await apiClient.put<CreditApplication>(url, data)
  return response.data
}

// React-Query Hooks
export function useSetApplicantCustomerReference(): [
  MutationFunction<CreditApplication, SetCustomerReferenceDto>,
  boolean,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: setApplicantCustomerReference,
    onSuccess: async (updatedCredit, params) => {
      const queryKey = fullCreditApplicationKeys.detail(params.creditApplicationId, params.financingProgramId)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          credit: transformCreditApplicationFromApi(updatedCredit),
        })
        await queryClient.invalidateQueries({
          queryKey: availableCreditKeys.byApplicationId(params.financingProgramId, params.creditApplicationId),
        })
      }
    },
  })
  return [mutateAsync, isPending]
}

export function useSetCoapplicantCustomerReference(): [
  MutationFunction<CreditApplication, SetCustomerReferenceDto>,
  boolean,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: setCoapplicantCustomerReference,
    onSuccess: async (updatedCredit, params) => {
      const queryKey = fullCreditApplicationKeys.detail(params.creditApplicationId, params.financingProgramId)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          credit: transformCreditApplicationFromApi(updatedCredit),
        })
        await queryClient.invalidateQueries({
          queryKey: availableCreditKeys.byApplicationId(params.financingProgramId, params.creditApplicationId),
        })
      }
    },
  })
  return [mutateAsync, isPending]
}
